// Низкоуровневый клиент API REGWAY CRM.
// Same-origin: все запросы идут на /api/v1 текущего хоста.
// - GET /api/v1/auth/csrf/ устанавливает cookie `csrftoken`; значение
//   перечитывается и отправляется заголовком X-CSRFToken для небезопасных методов.
// - 401 → уведомление подписчиков (переход на экран входа).
// - 403 → ApiError(code='forbidden') — экран «Доступ запрещён».
// - 409 → ApiError(code='conflict') — конфликт revision/идемпотентности.
// - Сетевые ошибки и 502/503/504 → повтор с экспоненциальной паузой.
// - 403 по CSRF → однократное обновление токена и повтор запроса.

export const API_BASE = '/api/v1';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const MAX_RETRIES = 2;
const RETRY_BASE_MS = 400;

export class ApiError extends Error {
  status: number;
  code: string;
  fieldErrors: unknown;
  correlationId?: string | null;

  constructor(status: number, code: string, message: string, fieldErrors?: unknown, correlationId?: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors ?? null;
    this.correlationId = correlationId;
  }

  get isUnauthorized() { return this.status === 401; }
  get isForbidden() { return this.status === 403; }
  get isNotFound() { return this.status === 404; }
  get isConflict() { return this.status === 409; }
}

export function getCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

let csrfToken: string | null = null;
let csrfPromise: Promise<string | null> | null = null;

/** Запрашивает CSRF-токен у backend и запоминает его (cookie csrftoken). */
export async function ensureCsrf(force = false): Promise<string | null> {
  if (!force && csrfToken) return csrfToken;
  if (!force && csrfPromise) return csrfPromise;
  csrfPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/csrf/`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
      });
      // Токен может прийти и в теле ответа, и в cookie — читаем оба варианта.
      try {
        const data = await res.clone().json();
        if (data && typeof data === 'object') {
          const t = (data as Record<string, unknown>).csrfToken ?? (data as Record<string, unknown>).csrf_token;
          if (typeof t === 'string' && t) csrfToken = t;
        }
      } catch { /* тело не JSON — полагаемся на cookie */ }
      csrfToken = getCookie('csrftoken') ?? csrfToken;
      return csrfToken;
    } catch {
      return csrfToken;
    } finally {
      csrfPromise = null;
    }
  })();
  return csrfPromise;
}

export function resetCsrf() {
  csrfToken = null;
}

// --- Уведомление о 401 ---

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized(cb: UnauthorizedListener): () => void {
  unauthorizedListeners.add(cb);
  return () => unauthorizedListeners.delete(cb);
}

function emitUnauthorized() {
  unauthorizedListeners.forEach((cb) => {
    try { cb(); } catch { /* noop */ }
  });
}

// --- Идемпотентность команд ---

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `cmd-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// --- Базовый запрос ---

export interface RequestOptions {
  method?: string;
  params?: Record<string, unknown>;
  body?: unknown;
  headers?: Record<string, string>;
  idempotencyKey?: string;
  /** отключить авто-ретрай (по умолчанию ретраим безопасные методы и команды с Idempotency-Key) */
  noRetry?: boolean;
  /** вернуть сырой Response (например, для preview/blob) */
  raw?: boolean;
}

function buildUrl(path: string, params?: Record<string, unknown>): string {
  const url = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  if (!params) return url;
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    qs.set(key, String(value));
  }
  const s = qs.toString();
  return s ? `${url}${url.includes('?') ? '&' : '?'}${s}` : url;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

async function parseErrorResponse(res: Response): Promise<ApiError> {
  let code = 'error';
  let message = `Ошибка ${res.status}`;
  let fieldErrors: unknown = null;
  let correlationId: string | null = res.headers.get('x-correlation-id');
  try {
    const data = await res.clone().json();
    if (data && typeof data === 'object') {
      const d = data as Record<string, unknown>;
      if (typeof d.code === 'string') code = d.code;
      if (typeof d.message === 'string') message = d.message;
      else if (typeof d.detail === 'string') message = d.detail;
      if ('field_errors' in d) fieldErrors = d.field_errors;
      if (typeof d.correlation_id === 'string') correlationId = d.correlation_id;
      // DRF-формат без обёртки: {field: [errors]} — считаем ошибками полей.
      if (!d.code && !d.detail && !d.message) fieldErrors = d;
    }
  } catch {
    try {
      const text = await res.text();
      if (text) message = text.slice(0, 500);
    } catch { /* noop */ }
  }
  if (res.status === 401) code = code === 'error' ? 'unauthorized' : code;
  if (res.status === 403 && code === 'error') code = 'forbidden';
  if (res.status === 409 && code === 'error') code = 'conflict';
  return new ApiError(res.status, code, message, fieldErrors, correlationId);
}

export async function apiRequest<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const safe = SAFE_METHODS.has(method);
  const isCommand = !safe && Boolean(options.idempotencyKey);
  const canRetry = !options.noRetry && (safe || isCommand);

  if (!safe) await ensureCsrf();

  const headers: Record<string, string> = {
    'Accept': 'application/json',
    ...(options.headers ?? {}),
  };
  if (!safe && !isFormData(options.body) && options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (!safe && csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }
  if (options.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }

  const url = buildUrl(path, options.params);
  const body = options.body === undefined
    ? undefined
    : isFormData(options.body) ? options.body : JSON.stringify(options.body);

  let attempt = 0;
  let csrfRetried = false;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    let res: Response;
    try {
      res = await fetch(url, { method, headers, body, credentials: 'same-origin' });
    } catch (e) {
      if (canRetry && attempt < MAX_RETRIES) {
        attempt += 1;
        await sleep(RETRY_BASE_MS * attempt);
        continue;
      }
      throw new ApiError(0, 'network_error', 'Сервер недоступен. Проверьте соединение.');
    }

    if (res.ok) {
      if (options.raw) return res as unknown as T;
      if (res.status === 204) return undefined as T;
      const ct = res.headers.get('content-type') ?? '';
      if (ct.includes('application/json')) {
        try { return (await res.json()) as T; } catch { return undefined as T; }
      }
      return (await res.text()) as unknown as T;
    }

    // CSRF-ошибка: Django отвечает 403 с текстом про CSRF — обновляем токен и повторяем один раз.
    if (!safe && res.status === 403 && !csrfRetried) {
      const text = await res.clone().text().catch(() => '');
      if (/csrf/i.test(text)) {
        csrfRetried = true;
        await ensureCsrf(true);
        if (csrfToken) headers['X-CSRFToken'] = csrfToken;
        continue;
      }
    }

    if (canRetry && RETRYABLE_STATUSES.has(res.status) && attempt < MAX_RETRIES) {
      attempt += 1;
      await sleep(RETRY_BASE_MS * attempt);
      continue;
    }

    const err = await parseErrorResponse(res);
    if (err.isUnauthorized) emitUnauthorized();
    throw err;
  }
}

export function apiGet<T = unknown>(path: string, params?: Record<string, unknown>) {
  return apiRequest<T>(path, { method: 'GET', params });
}

export function apiPost<T = unknown>(path: string, body?: unknown, opts: RequestOptions = {}) {
  return apiRequest<T>(path, { ...opts, method: 'POST', body });
}

export function apiPatch<T = unknown>(path: string, body?: unknown, opts: RequestOptions = {}) {
  return apiRequest<T>(path, { ...opts, method: 'PATCH', body });
}

export function apiDelete<T = unknown>(path: string, opts: RequestOptions = {}) {
  return apiRequest<T>(path, { ...opts, method: 'DELETE' });
}

/** Командный POST с обязательным Idempotency-Key (если ключ не передан — генерируется). */
export function apiCommand<T = unknown>(path: string, body?: unknown, idempotencyKey?: string) {
  return apiPost<T>(path, body, { idempotencyKey: idempotencyKey ?? newIdempotencyKey() });
}

/**
 * Универсальная загрузка списка: понимает и пагинированный ответ DRF
 * ({count, results, ...}), и «голый» массив — зависит от настроек backend.
 */
export async function apiList<T = unknown>(path: string, params?: Record<string, unknown>): Promise<T[]> {
  const data = await apiGet<unknown>(path, params);
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown }).results)) {
    return (data as { results: T[] }).results;
  }
  return [];
}
